/**
 * Shared catalogues for currency, country, and rail dropdowns. One
 * authoritative source so the marketing pricing calculator, onboarding,
 * vendor portal, and in-app settings all match — labels, symbols, and
 * ordering stay consistent.
 *
 * `name` keys for currencies are i18n lookup keys; resolve via `t(name)`
 * so JP/EN/etc. users see localised names while the underlying code
 * stays ISO 4217 / ISO 3166-1 alpha-2.
 *
 * Ordering: JP-priority entries first, then alphabetical by English
 * name. 88 countries, 65 active ISO 4217 currencies — covers every
 * market the cross-border payables engine can pay into.
 */

export interface CurrencyMeta {
  code: string;       // ISO 4217 ('JPY')
  symbol: string;     // '¥'
  name: string;       // i18n key under `currencies.<code>`
  region: 'jp' | 'asia' | 'na' | 'eu' | 'oceania' | 'sa' | 'mea';
  decimals: number;   // 0 for JPY/KRW/IDR/VND/ISK/PYG/CLP, 2 for everyone else
}

export const CURRENCIES: CurrencyMeta[] = [
  // --- pinned (PlyEZ priority markets) ---
  { code: 'JPY', symbol: '¥',    name: 'currencies.JPY', region: 'jp',   decimals: 0 },
  { code: 'USD', symbol: '$',    name: 'currencies.USD', region: 'na',   decimals: 2 },
  { code: 'EUR', symbol: '€',    name: 'currencies.EUR', region: 'eu',   decimals: 2 },
  { code: 'GBP', symbol: '£',    name: 'currencies.GBP', region: 'eu',   decimals: 2 },

  // --- Americas ---
  { code: 'CAD', symbol: 'C$',   name: 'currencies.CAD', region: 'na',   decimals: 2 },
  { code: 'MXN', symbol: 'Mex$', name: 'currencies.MXN', region: 'na',   decimals: 2 },
  { code: 'BRL', symbol: 'R$',   name: 'currencies.BRL', region: 'sa',   decimals: 2 },
  { code: 'ARS', symbol: 'AR$',  name: 'currencies.ARS', region: 'sa',   decimals: 2 },
  { code: 'CLP', symbol: 'CLP$', name: 'currencies.CLP', region: 'sa',   decimals: 0 },
  { code: 'COP', symbol: 'COL$', name: 'currencies.COP', region: 'sa',   decimals: 2 },
  { code: 'PEN', symbol: 'S/.',  name: 'currencies.PEN', region: 'sa',   decimals: 2 },
  { code: 'UYU', symbol: '$U',   name: 'currencies.UYU', region: 'sa',   decimals: 2 },
  { code: 'PYG', symbol: '₲',    name: 'currencies.PYG', region: 'sa',   decimals: 0 },
  { code: 'DOP', symbol: 'RD$',  name: 'currencies.DOP', region: 'na',   decimals: 2 },

  // --- Europe ---
  { code: 'CHF', symbol: 'Fr',   name: 'currencies.CHF', region: 'eu',   decimals: 2 },
  { code: 'SEK', symbol: 'kr',   name: 'currencies.SEK', region: 'eu',   decimals: 2 },
  { code: 'NOK', symbol: 'kr',   name: 'currencies.NOK', region: 'eu',   decimals: 2 },
  { code: 'DKK', symbol: 'kr',   name: 'currencies.DKK', region: 'eu',   decimals: 2 },
  { code: 'ISK', symbol: 'kr',   name: 'currencies.ISK', region: 'eu',   decimals: 0 },
  { code: 'PLN', symbol: 'zł',   name: 'currencies.PLN', region: 'eu',   decimals: 2 },
  { code: 'CZK', symbol: 'Kč',   name: 'currencies.CZK', region: 'eu',   decimals: 2 },
  { code: 'HUF', symbol: 'Ft',   name: 'currencies.HUF', region: 'eu',   decimals: 2 },
  { code: 'RON', symbol: 'lei',  name: 'currencies.RON', region: 'eu',   decimals: 2 },
  { code: 'BGN', symbol: 'лв',   name: 'currencies.BGN', region: 'eu',   decimals: 2 },
  { code: 'HRK', symbol: 'kn',   name: 'currencies.HRK', region: 'eu',   decimals: 2 },
  { code: 'RSD', symbol: 'дин',  name: 'currencies.RSD', region: 'eu',   decimals: 2 },
  { code: 'UAH', symbol: '₴',    name: 'currencies.UAH', region: 'eu',   decimals: 2 },
  { code: 'TRY', symbol: '₺',    name: 'currencies.TRY', region: 'eu',   decimals: 2 },
  { code: 'RUB', symbol: '₽',    name: 'currencies.RUB', region: 'eu',   decimals: 2 },
  { code: 'GEL', symbol: '₾',    name: 'currencies.GEL', region: 'eu',   decimals: 2 },

  // --- APAC ---
  { code: 'AUD', symbol: 'A$',   name: 'currencies.AUD', region: 'oceania', decimals: 2 },
  { code: 'NZD', symbol: 'NZ$',  name: 'currencies.NZD', region: 'oceania', decimals: 2 },
  { code: 'SGD', symbol: 'S$',   name: 'currencies.SGD', region: 'asia', decimals: 2 },
  { code: 'HKD', symbol: 'HK$',  name: 'currencies.HKD', region: 'asia', decimals: 2 },
  { code: 'KRW', symbol: '₩',    name: 'currencies.KRW', region: 'asia', decimals: 0 },
  { code: 'CNY', symbol: '¥',    name: 'currencies.CNY', region: 'asia', decimals: 2 },
  { code: 'TWD', symbol: 'NT$',  name: 'currencies.TWD', region: 'asia', decimals: 2 },
  { code: 'INR', symbol: '₹',    name: 'currencies.INR', region: 'asia', decimals: 2 },
  { code: 'IDR', symbol: 'Rp',   name: 'currencies.IDR', region: 'asia', decimals: 0 },
  { code: 'THB', symbol: '฿',    name: 'currencies.THB', region: 'asia', decimals: 2 },
  { code: 'PHP', symbol: '₱',    name: 'currencies.PHP', region: 'asia', decimals: 2 },
  { code: 'VND', symbol: '₫',    name: 'currencies.VND', region: 'asia', decimals: 0 },
  { code: 'MYR', symbol: 'RM',   name: 'currencies.MYR', region: 'asia', decimals: 2 },
  { code: 'KHR', symbol: '៛',    name: 'currencies.KHR', region: 'asia', decimals: 2 },
  { code: 'BDT', symbol: '৳',    name: 'currencies.BDT', region: 'asia', decimals: 2 },
  { code: 'PKR', symbol: '₨',    name: 'currencies.PKR', region: 'asia', decimals: 2 },
  { code: 'LKR', symbol: 'Rs',   name: 'currencies.LKR', region: 'asia', decimals: 2 },
  { code: 'KZT', symbol: '₸',    name: 'currencies.KZT', region: 'asia', decimals: 2 },
  { code: 'UZS', symbol: "so'm", name: 'currencies.UZS', region: 'asia', decimals: 2 },

  // --- MENA ---
  { code: 'AED', symbol: 'د.إ',  name: 'currencies.AED', region: 'mea', decimals: 2 },
  { code: 'SAR', symbol: 'ر.س',  name: 'currencies.SAR', region: 'mea', decimals: 2 },
  { code: 'QAR', symbol: 'ر.ق',  name: 'currencies.QAR', region: 'mea', decimals: 2 },
  { code: 'KWD', symbol: 'د.ك',  name: 'currencies.KWD', region: 'mea', decimals: 2 },
  { code: 'BHD', symbol: '.د.ب', name: 'currencies.BHD', region: 'mea', decimals: 2 },
  { code: 'OMR', symbol: 'ر.ع',  name: 'currencies.OMR', region: 'mea', decimals: 2 },
  { code: 'JOD', symbol: 'د.ا',  name: 'currencies.JOD', region: 'mea', decimals: 2 },
  { code: 'LBP', symbol: 'ل.ل',  name: 'currencies.LBP', region: 'mea', decimals: 2 },
  { code: 'EGP', symbol: 'ج.م',  name: 'currencies.EGP', region: 'mea', decimals: 2 },
  { code: 'ILS', symbol: '₪',    name: 'currencies.ILS', region: 'mea', decimals: 2 },
  { code: 'MAD', symbol: 'DH',   name: 'currencies.MAD', region: 'mea', decimals: 2 },

  // --- Africa ---
  { code: 'ZAR', symbol: 'R',    name: 'currencies.ZAR', region: 'mea', decimals: 2 },
  { code: 'NGN', symbol: '₦',    name: 'currencies.NGN', region: 'mea', decimals: 2 },
  { code: 'KES', symbol: 'KSh',  name: 'currencies.KES', region: 'mea', decimals: 2 },
  { code: 'GHS', symbol: 'GH₵',  name: 'currencies.GHS', region: 'mea', decimals: 2 },
  { code: 'TZS', symbol: 'TSh',  name: 'currencies.TZS', region: 'mea', decimals: 2 },
];

export function currency(code: string): CurrencyMeta | undefined {
  return CURRENCIES.find((c) => c.code === code.toUpperCase());
}

export interface CountryMeta {
  code: string;       // ISO 3166-1 alpha-2
  emoji: string;      // 🇯🇵
  defaultCurrency: string;
  defaultLocale: string;
}

/**
 * Countries we surface in onboarding + vendor portal selectors.
 * JP/US/GB/DE pinned first (PlyEZ priority markets), then alphabetical
 * by English name. 88 entries.
 */
export const COUNTRIES: CountryMeta[] = [
  // --- pinned ---
  { code: 'JP', emoji: '🇯🇵', defaultCurrency: 'JPY', defaultLocale: 'ja' },
  { code: 'US', emoji: '🇺🇸', defaultCurrency: 'USD', defaultLocale: 'en' },
  { code: 'GB', emoji: '🇬🇧', defaultCurrency: 'GBP', defaultLocale: 'en' },
  { code: 'DE', emoji: '🇩🇪', defaultCurrency: 'EUR', defaultLocale: 'de' },

  // --- rest, alphabetical by English name ---
  { code: 'AR', emoji: '🇦🇷', defaultCurrency: 'ARS', defaultLocale: 'es' },
  { code: 'AU', emoji: '🇦🇺', defaultCurrency: 'AUD', defaultLocale: 'en' },
  { code: 'AT', emoji: '🇦🇹', defaultCurrency: 'EUR', defaultLocale: 'de' },
  { code: 'BH', emoji: '🇧🇭', defaultCurrency: 'BHD', defaultLocale: 'ar' },
  { code: 'BD', emoji: '🇧🇩', defaultCurrency: 'BDT', defaultLocale: 'bn' },
  { code: 'BE', emoji: '🇧🇪', defaultCurrency: 'EUR', defaultLocale: 'fr' },
  { code: 'BR', emoji: '🇧🇷', defaultCurrency: 'BRL', defaultLocale: 'pt' },
  { code: 'BG', emoji: '🇧🇬', defaultCurrency: 'BGN', defaultLocale: 'en' },
  { code: 'KH', emoji: '🇰🇭', defaultCurrency: 'KHR', defaultLocale: 'en' },
  { code: 'CA', emoji: '🇨🇦', defaultCurrency: 'CAD', defaultLocale: 'en' },
  { code: 'CL', emoji: '🇨🇱', defaultCurrency: 'CLP', defaultLocale: 'es' },
  { code: 'CN', emoji: '🇨🇳', defaultCurrency: 'CNY', defaultLocale: 'zh' },
  { code: 'CO', emoji: '🇨🇴', defaultCurrency: 'COP', defaultLocale: 'es' },
  { code: 'CR', emoji: '🇨🇷', defaultCurrency: 'USD', defaultLocale: 'es' },
  { code: 'HR', emoji: '🇭🇷', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'CY', emoji: '🇨🇾', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'CZ', emoji: '🇨🇿', defaultCurrency: 'CZK', defaultLocale: 'en' },
  { code: 'DK', emoji: '🇩🇰', defaultCurrency: 'DKK', defaultLocale: 'en' },
  { code: 'DO', emoji: '🇩🇴', defaultCurrency: 'DOP', defaultLocale: 'es' },
  { code: 'EC', emoji: '🇪🇨', defaultCurrency: 'USD', defaultLocale: 'es' },
  { code: 'EG', emoji: '🇪🇬', defaultCurrency: 'EGP', defaultLocale: 'ar' },
  { code: 'EE', emoji: '🇪🇪', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'FI', emoji: '🇫🇮', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'FR', emoji: '🇫🇷', defaultCurrency: 'EUR', defaultLocale: 'fr' },
  { code: 'GE', emoji: '🇬🇪', defaultCurrency: 'GEL', defaultLocale: 'en' },
  { code: 'GH', emoji: '🇬🇭', defaultCurrency: 'GHS', defaultLocale: 'en' },
  { code: 'GR', emoji: '🇬🇷', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'GT', emoji: '🇬🇹', defaultCurrency: 'USD', defaultLocale: 'es' },
  { code: 'HK', emoji: '🇭🇰', defaultCurrency: 'HKD', defaultLocale: 'zh' },
  { code: 'HU', emoji: '🇭🇺', defaultCurrency: 'HUF', defaultLocale: 'en' },
  { code: 'IS', emoji: '🇮🇸', defaultCurrency: 'ISK', defaultLocale: 'en' },
  { code: 'IN', emoji: '🇮🇳', defaultCurrency: 'INR', defaultLocale: 'hi' },
  { code: 'ID', emoji: '🇮🇩', defaultCurrency: 'IDR', defaultLocale: 'id' },
  { code: 'IE', emoji: '🇮🇪', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'IL', emoji: '🇮🇱', defaultCurrency: 'ILS', defaultLocale: 'en' },
  { code: 'IT', emoji: '🇮🇹', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'JO', emoji: '🇯🇴', defaultCurrency: 'JOD', defaultLocale: 'ar' },
  { code: 'KZ', emoji: '🇰🇿', defaultCurrency: 'KZT', defaultLocale: 'ru' },
  { code: 'KE', emoji: '🇰🇪', defaultCurrency: 'KES', defaultLocale: 'en' },
  { code: 'KR', emoji: '🇰🇷', defaultCurrency: 'KRW', defaultLocale: 'ko' },
  { code: 'KW', emoji: '🇰🇼', defaultCurrency: 'KWD', defaultLocale: 'ar' },
  { code: 'LV', emoji: '🇱🇻', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'LB', emoji: '🇱🇧', defaultCurrency: 'LBP', defaultLocale: 'ar' },
  { code: 'LT', emoji: '🇱🇹', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'LU', emoji: '🇱🇺', defaultCurrency: 'EUR', defaultLocale: 'fr' },
  { code: 'MO', emoji: '🇲🇴', defaultCurrency: 'HKD', defaultLocale: 'zh' },
  { code: 'MY', emoji: '🇲🇾', defaultCurrency: 'MYR', defaultLocale: 'en' },
  { code: 'MT', emoji: '🇲🇹', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'MX', emoji: '🇲🇽', defaultCurrency: 'MXN', defaultLocale: 'es' },
  { code: 'MA', emoji: '🇲🇦', defaultCurrency: 'MAD', defaultLocale: 'ar' },
  { code: 'NL', emoji: '🇳🇱', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'NZ', emoji: '🇳🇿', defaultCurrency: 'NZD', defaultLocale: 'en' },
  { code: 'NG', emoji: '🇳🇬', defaultCurrency: 'NGN', defaultLocale: 'en' },
  { code: 'NO', emoji: '🇳🇴', defaultCurrency: 'NOK', defaultLocale: 'en' },
  { code: 'OM', emoji: '🇴🇲', defaultCurrency: 'OMR', defaultLocale: 'ar' },
  { code: 'PK', emoji: '🇵🇰', defaultCurrency: 'PKR', defaultLocale: 'ur' },
  { code: 'PA', emoji: '🇵🇦', defaultCurrency: 'USD', defaultLocale: 'es' },
  { code: 'PY', emoji: '🇵🇾', defaultCurrency: 'PYG', defaultLocale: 'es' },
  { code: 'PE', emoji: '🇵🇪', defaultCurrency: 'PEN', defaultLocale: 'es' },
  { code: 'PH', emoji: '🇵🇭', defaultCurrency: 'PHP', defaultLocale: 'en' },
  { code: 'PL', emoji: '🇵🇱', defaultCurrency: 'PLN', defaultLocale: 'en' },
  { code: 'PT', emoji: '🇵🇹', defaultCurrency: 'EUR', defaultLocale: 'pt' },
  { code: 'QA', emoji: '🇶🇦', defaultCurrency: 'QAR', defaultLocale: 'ar' },
  { code: 'RO', emoji: '🇷🇴', defaultCurrency: 'RON', defaultLocale: 'en' },
  { code: 'RU', emoji: '🇷🇺', defaultCurrency: 'RUB', defaultLocale: 'ru' },
  { code: 'SA', emoji: '🇸🇦', defaultCurrency: 'SAR', defaultLocale: 'ar' },
  { code: 'RS', emoji: '🇷🇸', defaultCurrency: 'RSD', defaultLocale: 'en' },
  { code: 'SG', emoji: '🇸🇬', defaultCurrency: 'SGD', defaultLocale: 'en' },
  { code: 'SK', emoji: '🇸🇰', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'SI', emoji: '🇸🇮', defaultCurrency: 'EUR', defaultLocale: 'en' },
  { code: 'ZA', emoji: '🇿🇦', defaultCurrency: 'ZAR', defaultLocale: 'en' },
  { code: 'ES', emoji: '🇪🇸', defaultCurrency: 'EUR', defaultLocale: 'es' },
  { code: 'LK', emoji: '🇱🇰', defaultCurrency: 'LKR', defaultLocale: 'en' },
  { code: 'SE', emoji: '🇸🇪', defaultCurrency: 'SEK', defaultLocale: 'en' },
  { code: 'CH', emoji: '🇨🇭', defaultCurrency: 'CHF', defaultLocale: 'de' },
  { code: 'TW', emoji: '🇹🇼', defaultCurrency: 'TWD', defaultLocale: 'zh' },
  { code: 'TZ', emoji: '🇹🇿', defaultCurrency: 'TZS', defaultLocale: 'en' },
  { code: 'TH', emoji: '🇹🇭', defaultCurrency: 'THB', defaultLocale: 'en' },
  { code: 'TR', emoji: '🇹🇷', defaultCurrency: 'TRY', defaultLocale: 'en' },
  { code: 'UA', emoji: '🇺🇦', defaultCurrency: 'UAH', defaultLocale: 'ru' },
  { code: 'AE', emoji: '🇦🇪', defaultCurrency: 'AED', defaultLocale: 'ar' },
  { code: 'UY', emoji: '🇺🇾', defaultCurrency: 'UYU', defaultLocale: 'es' },
  { code: 'UZ', emoji: '🇺🇿', defaultCurrency: 'UZS', defaultLocale: 'ru' },
  { code: 'VN', emoji: '🇻🇳', defaultCurrency: 'VND', defaultLocale: 'en' },
];

export function country(code: string): CountryMeta | undefined {
  return COUNTRIES.find((c) => c.code === code.toUpperCase());
}

/**
 * Payment rails available on the platform — used in vendor onboarding +
 * payment-run UI. Order = "most common first for the active locale".
 */
export interface RailMeta {
  code: 'zengin' | 'swift' | 'ach' | 'sepa' | 'wise';
  label: string;        // i18n key
  description: string;  // i18n key
  region: 'jp' | 'na' | 'eu' | 'global';
}

export const RAILS: RailMeta[] = [
  { code: 'zengin', label: 'rails.zengin.label', description: 'rails.zengin.body', region: 'jp' },
  { code: 'ach',    label: 'rails.ach.label',    description: 'rails.ach.body',    region: 'na' },
  { code: 'sepa',   label: 'rails.sepa.label',   description: 'rails.sepa.body',   region: 'eu' },
  { code: 'swift',  label: 'rails.swift.label',  description: 'rails.swift.body',  region: 'global' },
  { code: 'wise',   label: 'rails.wise.label',   description: 'rails.wise.body',   region: 'global' },
];
